import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiFetch from "../../utlis/apiFetch";
import { forceLogout } from "../../utlis/forceLogout";

const handleAuthError = (error, rejectWithValue) => {
  if (error.message === "Your session has expired") forceLogout();
  return rejectWithValue(error.message);
};

// Async thunk to fetch all leads
export const fetchLeads = createAsyncThunk(
  "leads/fetchLeads",
  async (overrides = {}, { getState, rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const state = getState().leads;
      const {
        fromDate,
        toDate,
        countries,
        visaCategory,
        visaExpiryDays,
        processType,
        page,
        limit,
        status,
        searchTerm,
        agentIds,
      } = { ...state.filters, ...overrides };

      // Build query params using URLSearchParams for consistent encoding
      // Countries comes first as per API requirement
      const params = new URLSearchParams();
      if (countries && countries.length) params.append('countries', countries.join(","));
      if (status && status !== "All") params.append('status', status);
      if (visaCategory && visaCategory !== "All") params.append('visaCategory', visaCategory);
      if (Number(visaExpiryDays) > 0) params.append('visaExpiryDays', String(visaExpiryDays));
      if (processType) params.append('processType', processType);
      if (agentIds && agentIds.length) params.append('agentId', agentIds.join(","));
      if (fromDate) params.append('fromDate', fromDate);
      if (toDate) params.append('toDate', toDate);
      if (searchTerm && searchTerm.trim()) params.append('searchString', searchTerm.trim());
      if (page) params.append('page', page);
      if (limit) params.append('limit', limit);
      // Add timestamp to prevent caching
      params.append('_t', Date.now());

      const url = `${process.env.REACT_APP_API_BASE_URL}/api/application/allApplications?${params.toString()}`;

      const response = await apiFetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = response._data ?? await response.json();
      if (!response.ok) {
        // Treat 404 with empty body as empty successful response
        if (response.status === 404) {
          const body = data.body || [];
          const isEmpty = Array.isArray(body) && body.length === 0;
          if (isEmpty) {
            return { applications: [], totalApplications: 0, totalPages: 1 };
          }
        }
        throw new Error(data.message || "Failed to fetch leads");
      }

      const body = data.body || {};
      const applications = Array.isArray(body.applications) ? body.applications : (Array.isArray(body) ? body : []);
      const totalApplications = body.totalApplications || applications.length || 0;
      const limitNum = Number(limit) || 10;
      const totalPages = body.totalPages || Math.max(1, Math.ceil(totalApplications / limitNum));

      return { applications, totalApplications, totalPages };
    } catch (err) {
      return handleAuthError(err, rejectWithValue);
    }
  }
);

// Async thunk to update an application with reason (URL-encoded payload)
export const updateApplicationWithReason = createAsyncThunk(
  "applications/updateWithReason",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found.");

      const body = new URLSearchParams();
      Object.entries(formData || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null) body.append(k, String(v));
      });

      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/application/updateApplication/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${token}`,
          },
          body,
        }
      );

      const data = response._data ?? await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to update application");

      return data.body;
    } catch (error) {
      return handleAuthError(error, rejectWithValue);
    }
  }
);

// Async thunk to assign an application to an agent
export const assignApplication = createAsyncThunk(
  "leads/assignApplication",
  async ({ applicationId, agentId, reason }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/application/assignApplication`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${token}`,
          },
          body: new URLSearchParams({
            applicationId,
            agentId,
            reason,
          }),
        }
      );

      const data = response._data ?? await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to assign application");
      }

      return data.body;
    } catch (err) {
      return handleAuthError(err, rejectWithValue);
    }
  }
);

// Async thunk to update an application
export const updateApplication = createAsyncThunk(
  "applications/update",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found.");

      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/application/updateApplication/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(formData),
        }
      );

      const data = response._data ?? await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to update application");

      return data.body;
    } catch (error) {
      return handleAuthError(error, rejectWithValue);
    }
  }
);

// Async thunk to update application status
export const updateApplicationStatus = createAsyncThunk(
  "applications/updateStatus",
  async ({ id, paymentStatus }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found.");

      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/application/updateApplication/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ paymentStatus }),
        }
      );

      const data = response._data ?? await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to update status");

      return data.body;
    } catch (error) {
      return handleAuthError(error, rejectWithValue);
    }
  }
);

// Async thunk to reopen a finalized application
export const reopenApplication = createAsyncThunk(
  "applications/reopen",
  async ({ applicationId, agentId, applicationType, reason, isFCC, isART }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found. Please log in.");

      const params = new URLSearchParams();
      params.append("applicationId", applicationId);
      if (agentId) params.append("agentId", agentId);
      if (reason) params.append("reason", reason);
      if (applicationType) params.append("applicationType", applicationType);
      if (isFCC) params.append("isFCC", "true");
      if (isART) params.append("isART", "true");

      const url = `${process.env.REACT_APP_API_BASE_URL}/api/application/reOpenApplication?${params.toString()}`;

      const response = await apiFetch(url, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response._data ?? await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to reopen application");

      return data.body;
    } catch (error) {
      return handleAuthError(error, rejectWithValue);
    }
  }
);

// Async thunk to update an application without showing a loader
export const updateApplicationNoLoader = createAsyncThunk(
  "applications/updateNoLoader",
  async ({ id, formData, isFileUpload }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found. Please log in.");

      const headers = isFileUpload
        ? { Authorization: `Bearer ${token}` }
        : {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        };
      const body = isFileUpload ? formData : JSON.stringify(formData);

      const response = await apiFetch(`${process.env.REACT_APP_API_BASE_URL}/api/application/updateApplication/${id}`, {
        method: "PUT",
        headers,
        body,
      });

      const data = response._data ?? await response.json();
      if (!response.ok) throw new Error(data.message || "Something went wrong");

      // Always pass preventLoader as true
      return { data, preventLoader: true };
    } catch (error) {
      return handleAuthError(error, rejectWithValue);
    }
  }
);

// Async thunk to delete a file
export const deleteFile = createAsyncThunk(
  "applications/deleteFile",
  async ({ applicationId, category, fileId }, { rejectWithValue }) => {
    const formData = new URLSearchParams()
    formData.append("applicationId", applicationId)
    formData.append("category", category)
    formData.append("fileId", fileId)

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found. Please log in.")

      const response = await apiFetch(`${process.env.REACT_APP_API_BASE_URL}/api/application/deleteFile`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      })

      const data = response._data ?? await response.json()
      if (!response.ok) throw new Error(data.message || "Something went wrong")

      return data
    } catch (error) {
      return handleAuthError(error, rejectWithValue)
    }
  },
)

// The leads slice
const adminLeadSlice = createSlice({
  name: "leads",
  initialState: {
    leads: [],
    isLoading: false,
    isAssigning: false,
    isUpdating: false,
    isUpdatingStatus: false,
    isDeleting: false, // Add isDeleting state for deleteFile
    error: null,
    assignError: null,
    updateError: null,
    totalPages: 1,
    totalApplications: 0,
    filters: {
      fromDate: "",
      toDate: "",
      countries: [],
      visaCategory: "All",
      visaExpiryDays: "",
      processType: "",
      status: "All",
      page: 1,
      limit: 10,
      searchTerm: "",
      agentIds: [],
    },
  },
  reducers: {
    clearLeads: (state) => {
      state.leads = [];
      state.totalPages = 1;
      state.totalApplications = 0;
    },
    setLeadsFilters: (state, action) => {
      const keys = Object.keys(action.payload);
      const onlyPageOrLimit = keys.every((k) => k === "page" || k === "limit");

      let hasFilterChanged = false;
      if (!onlyPageOrLimit) {
        hasFilterChanged = keys.some((key) => {
          if (key === "page" || key === "limit") return false;
          
          const newVal = action.payload[key];
          const oldVal = state.filters[key];

          // Array comparison (e.g. countries)
          if (Array.isArray(newVal) && Array.isArray(oldVal)) {
            if (newVal.length !== oldVal.length) return true;
            // Simple string array sort & compare or JSON stringify
            // Assuming simple string arrays for filters
            return JSON.stringify(newVal) !== JSON.stringify(oldVal);
          }
          
          return newVal !== oldVal;
        });
      }
      
      // Clear leads when filters change (except page/limit) to prevent showing stale data
      if (!onlyPageOrLimit && hasFilterChanged) {
        state.leads = [];
        state.filters.page = 1;
      }
      
      state.filters = { ...state.filters, ...action.payload };
    },
    setLeadsPage: (state, action) => {
      state.filters.page = action.payload || 1;
    },
    setLeadsLimit: (state, action) => {
      state.filters.limit = action.payload || 10;
      state.filters.page = 1;
    },
    setLeadsFiltersNoPageReset: (state, action) => {
      // Similar to setLeadsFilters but doesn't reset page when filters change
      const keys = Object.keys(action.payload);
      
      let hasFilterChanged = false;
      if (!keys.every((k) => k === "page" || k === "limit")) {
        hasFilterChanged = keys.some((key) => {
          if (key === "page" || key === "limit") return false;
          
          const newVal = action.payload[key];
          const oldVal = state.filters[key];

          // Array comparison (e.g. countries)
          if (Array.isArray(newVal) && Array.isArray(oldVal)) {
            if (newVal.length !== oldVal.length) return true;
            return JSON.stringify(newVal) !== JSON.stringify(oldVal);
          }
          
          return newVal !== oldVal;
        });
      }
      
      // Clear leads when filters change, but preserve the page
      if (hasFilterChanged) {
        state.leads = [];
      }
      
      state.filters = { ...state.filters, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch leads
      .addCase(fetchLeads.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        state.isLoading = false;
        state.leads = action.payload.applications || [];
        state.totalPages = action.payload.totalPages || 1;
        state.totalApplications = action.payload.totalApplications || 0;
      })
      .addCase(fetchLeads.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Assign application
      .addCase(assignApplication.pending, (state) => {
        state.isAssigning = true;
        state.assignError = null;
      })
      .addCase(assignApplication.fulfilled, (state, action) => {
        state.isAssigning = false;
        const updatedApp = action.payload;
        state.leads = state.leads.map((lead) =>
          lead._id === updatedApp._id ? updatedApp : lead
        );
      })
      .addCase(assignApplication.rejected, (state, action) => {
        state.isAssigning = false;
        state.assignError = action.payload;
      })

      // Update application
      .addCase(updateApplication.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
      })
      .addCase(updateApplication.fulfilled, (state, action) => {
        state.isUpdating = false;
        const updatedApp = action.payload;
        state.leads = state.leads.map((lead) =>
          lead._id === updatedApp._id ? updatedApp : lead
        );
      })
      .addCase(updateApplication.rejected, (state, action) => {
        state.isUpdating = false;
        state.updateError = action.payload;
      })

      // Update application status
      .addCase(updateApplicationStatus.pending, (state) => {
        state.isUpdatingStatus = true;
        state.updateError = null;
      })
      .addCase(updateApplicationStatus.fulfilled, (state, action) => {
        state.isUpdatingStatus = false;
        const updatedApp = action.payload;
        const isTerminal = [
          "Finalized",
          "Rejected",
          "Cancelled",
          "Withdrawn",
          "WithdrawnByAgent",
          "WithdrawnByClient",
        ].includes(String(updatedApp?.status || updatedApp?.visaStatus || ""));
        if (isTerminal) {
          state.leads = state.leads.filter((lead) => lead._id !== updatedApp._id);
        } else {
          state.leads = state.leads.map((lead) =>
            lead._id === updatedApp._id ? updatedApp : lead
          );
        }
      })
      .addCase(updateApplicationStatus.rejected, (state, action) => {
        state.isUpdatingStatus = false;
        state.updateError = action.payload;
      })

      // Update with reason
      .addCase(updateApplicationWithReason.fulfilled, (state, action) => {
        const updatedApp = action.payload;
        const isTerminal = [
          "Finalized",
          "Rejected",
          "Cancelled",
          "Withdrawn",
          "WithdrawnByAgent",
          "WithdrawnByClient",
          "Deleted",
        ].includes(String(updatedApp?.status || updatedApp?.visaStatus || ""));
        if (isTerminal) {
          state.leads = state.leads.filter((lead) => lead._id !== updatedApp._id);
        } else {
          state.leads = state.leads.map((lead) =>
            lead._id === updatedApp._id ? updatedApp : lead
          );
        }
      })

      // Reopen application
      .addCase(reopenApplication.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
      })
      .addCase(reopenApplication.fulfilled, (state, action) => {
        state.isUpdating = false;
        const reopened = action.payload;
        // Ensure the reopened application appears in leads with updated data
        const idx = state.leads.findIndex((l) => l._id === reopened._id);
        if (idx !== -1) {
          state.leads[idx] = reopened;
        } else {
          state.leads.unshift(reopened);
        }
      })
      .addCase(reopenApplication.rejected, (state, action) => {
        state.isUpdating = false;
        state.updateError = action.payload;
      })

      // Add the case handlers for updateApplicationNoLoader in the extraReducers section
      .addCase(updateApplicationNoLoader.pending, (state) => {
        // Don't set isLoading to true
        state.updateError = null;
      })
      .addCase(updateApplicationNoLoader.fulfilled, (state, action) => {
        const updatedApplication = action.payload.data.body;
        // Always update the application in the state, don't remove it
        // This prevents triggering unnecessary API refetches when status changes to terminal states
        state.leads = state.leads.map((lead) =>
          lead._id === updatedApplication._id ? updatedApplication : lead
        );
      })
      .addCase(updateApplicationNoLoader.rejected, (state, action) => {
        state.updateError = action.payload;
      })

      // Add the case handlers for deleteFile
      .addCase(deleteFile.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deleteFile.fulfilled, (state, action) => {
        state.isDeleting = false;
        const updatedApplication = action.payload.body;

        const index = state.leads.findIndex((lead) => lead._id === updatedApplication._id);
        if (index !== -1) {
          state.leads[index] = updatedApplication;
        }
      })
      .addCase(deleteFile.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      });
  },
});

// Export actions and reducer
export const { clearLeads, setLeadsFilters, setLeadsPage, setLeadsLimit, setLeadsFiltersNoPageReset } = adminLeadSlice.actions;
export default adminLeadSlice.reducer;